// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/**
 * Domain vocabulary: outcomes, species, activities, states — labels and the
 * ONE colour each entity uses across every view (table pills, bars, map,
 * matrix, treemap). Defined once here, referenced everywhere.
 */
import type { Outcome } from './types';

// ── Outcome (the semantic spine of the site) ──────────────────────────────────
export const OUTCOME_ORDER: Outcome[] = ['fatal', 'injured', 'uninjured', 'unknown'];
export const OUTCOME_LABEL: Record<Outcome, string> = {
  fatal: 'Fatal',
  injured: 'Injured',
  uninjured: 'Uninjured',
  unknown: 'Unknown',
};
export const OUTCOME_COLOUR: Record<Outcome, string> = {
  fatal: '#b3202f', // deep red
  injured: '#e08a1e', // amber
  uninjured: '#2a9d8f', // teal-green — survived unharmed
  unknown: '#94a3b8', // slate
};

// ── Species ───────────────────────────────────────────────────────────────────
export interface SpeciesInfo {
  label: string;
  sci: string;
  colour: string;
  big3: boolean;
  note: string;
}
export const SPECIES: Record<string, SpeciesInfo> = {
  white: {
    label: 'White shark',
    sci: 'Carcharodon carcharias',
    colour: '#3a6ea5',
    big3: true,
    note: 'The great white — large, powerful, and behind more fatal bites than any other species in Australia. Often a single exploratory bite of a surfer or swimmer in cooler southern waters.',
  },
  tiger: {
    label: 'Tiger shark',
    sci: 'Galeocerdo cuvier',
    colour: '#c98a2b',
    big3: true,
    note: 'A large tropical and subtropical shark with the highest fatal-bite ratio of the three. Indiscriminate feeders, most active in warmer northern waters.',
  },
  bull: {
    label: 'Bull shark',
    sci: 'Carcharhinus leucas',
    colour: '#5a6b7a',
    big3: true,
    note: 'Tolerates fresh and brackish water and ranges far up rivers and estuaries — the species most associated with bites in murky, inshore and river settings.',
  },
  wobbegong: {
    label: 'Wobbegong',
    sci: 'Orectolobus spp.',
    colour: '#8a9a5b',
    big3: false,
    note: 'A carpet shark that rests camouflaged on the bottom. Bites are almost always defensive nips when trodden on or handled — no recorded fatalities in this dataset.',
  },
  whaler: {
    label: 'Whaler shark',
    sci: 'Carcharhinus spp.',
    colour: '#7d8fa3',
    big3: false,
    note: 'A group of similar-looking requiem sharks often reported only as "whaler". Common along the coast; bites are usually non-fatal.',
  },
  'bronze-whaler': {
    label: 'Bronze whaler',
    sci: 'Carcharhinus brachyurus',
    colour: '#9c7a4a',
    big3: false,
    note: 'A large temperate whaler (copper shark) that follows baitfish and salmon schools inshore, especially in South Australia and the south-west.',
  },
  dusky: {
    label: 'Dusky shark',
    sci: 'Carcharhinus obscurus',
    colour: '#6b7280',
    big3: false,
    note: 'A large, slow-growing whaler of continental shelves. Rarely implicated in bites.',
  },
  'grey-nurse': {
    label: 'Grey nurse shark',
    sci: 'Carcharias taurus',
    colour: '#78909c',
    big3: false,
    note: 'Fearsome-looking but placid; a protected, threatened species. Historic bites were largely provoked (spearfishing).',
  },
  'grey-reef': {
    label: 'Grey reef shark',
    sci: 'Carcharhinus amblyrhynchos',
    colour: '#4f7a6f',
    big3: false,
    note: 'A tropical reef shark that can be territorial toward divers on coral reefs in the north.',
  },
  'whitetip-reef': {
    label: 'Whitetip reef shark',
    sci: 'Triaenodon obesus',
    colour: '#5b8f86',
    big3: false,
    note: 'A small, common tropical reef shark. Bites are rare and minor.',
  },
  hammerhead: {
    label: 'Hammerhead',
    sci: 'Sphyrna spp.',
    colour: '#a06a8a',
    big3: false,
    note: 'Distinctive head shape; large species are capable of a serious bite but rarely recorded doing so here.',
  },
  mako: {
    label: 'Mako shark',
    sci: 'Isurus oxyrinchus',
    colour: '#4a7fa0',
    big3: false,
    note: 'The fastest shark; most incidents involve hooked fish brought aboard boats.',
  },
  sevengill: {
    label: 'Sevengill shark',
    sci: 'Notorynchus cepedianus',
    colour: '#6d7f8c',
    big3: false,
    note: 'A cool-water shark of bays and estuaries in the south.',
  },
  'port-jackson': {
    label: 'Port Jackson shark',
    sci: 'Heterodontus portusjacksoni',
    colour: '#9aa17a',
    big3: false,
    note: 'A small, harmless bottom-dwelling horn shark; bites are trivial and provoked.',
  },
  unidentified: {
    label: 'Unidentified / other',
    sci: '',
    colour: '#b0b8c1',
    big3: false,
    note: 'The species could not be confirmed — common in older records and in murky-water or quick encounters.',
  },
};
export function speciesInfo(key: string): SpeciesInfo {
  return SPECIES[key] ?? SPECIES.unidentified;
}
/** Species keys sorted so the three most-implicated appear first. */
export const SPECIES_KEYS = Object.keys(SPECIES);

// ── Activity ──────────────────────────────────────────────────────────────────
export const ACTIVITY_LABEL: Record<string, string> = {
  swimming: 'Swimming',
  'board-riding': 'Board-riding',
  snorkelling: 'Snorkelling',
  spearfishing: 'Spearfishing',
  diving: 'Diving',
  fishing: 'Fishing',
  boating: 'Boating',
  wading: 'Wading / standing',
  other: 'Other / unknown',
};
export const ACTIVITY_ORDER = ['swimming', 'board-riding', 'snorkelling', 'spearfishing', 'diving', 'fishing', 'boating', 'wading', 'other'];
export function activityLabel(key: string): string {
  return ACTIVITY_LABEL[key] ?? 'Other / unknown';
}

// ── States / territories ──────────────────────────────────────────────────────
export const STATE_ORDER = ['NSW', 'QLD', 'WA', 'SA', 'VIC', 'TAS', 'NT'];
export const STATE_NAME: Record<string, string> = {
  NSW: 'New South Wales',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  VIC: 'Victoria',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
  ACT: 'Aust. Capital Territory',
};
export const STATE_COLOUR: Record<string, string> = {
  NSW: '#1d6fb8',
  QLD: '#8e2f8e',
  WA: '#c9721f',
  SA: '#2f8f6b',
  VIC: '#2c4c8c',
  TAS: '#0e7c86',
  NT: '#b3202f',
  ACT: '#6b7280',
};
export function stateName(code: string): string {
  return STATE_NAME[code] ?? code;
}
export function stateColour(code: string): string {
  return STATE_COLOUR[code] ?? '#6b7280';
}
// 2021 ABS ERP resident population per state/territory — used ONLY for a rough
// "incidents per million residents" exposure proxy on the map (clearly labelled).
export const STATE_POP: Record<string, number> = {
  NSW: 8072000,
  VIC: 6503000,
  QLD: 5156000,
  WA: 2660000,
  SA: 1771000,
  TAS: 541000,
  NT: 246000,
  ACT: 431000,
};

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const SEASON_OF_MONTH: Record<number, string> = {
  12: 'Summer', 1: 'Summer', 2: 'Summer',
  3: 'Autumn', 4: 'Autumn', 5: 'Autumn',
  6: 'Winter', 7: 'Winter', 8: 'Winter',
  9: 'Spring', 10: 'Spring', 11: 'Spring',
};
